/**
 * @file frontend/src/layouts/AuthLayout.js
 * @module layouts
 * @component AuthLayout
 *
 * Auth Layout — Application layout wrapper providing chrome and navigation.
 *
 * Responsibilities:
 *   - Render the primary UI for this surface.
 *   - Coordinate API calls and local state.
 *   - Emit user actions to parent components / context where applicable.
 *
 * Notes:
 *   - All API calls go through the shared axios client (REACT_APP_BACKEND_URL).
 *   - Side effects belong in `useEffect`; keep render pure.
 */
import React from 'react';
import { Outlet, Link } from 'react-router-dom';

const AuthLayout = () => {
  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 p-12 flex-col justify-between relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1731349219592-60ca16964631?w=1920&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity w-fit">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/30">
              <span className="text-white font-bold text-xl">K</span>
            </div>
            <span className="text-white text-2xl font-bold font-heading">K12 ERP</span>
          </Link>
        </div>
        <div className="relative z-10 space-y-6">
          <div className="inline-flex items-center gap-2 bg-blue-600/20 text-blue-300 px-3 py-1.5 rounded-full text-sm font-medium backdrop-blur-sm border border-blue-500/30">
            ⚡ First AI-Driven ERP for K-12
          </div>
          <h1 className="text-4xl font-bold text-white font-heading leading-tight">
            AI-Powered Platform.<br />Millions in Savings.
          </h1>
          <p className="text-slate-300 text-lg max-w-md">
            Automate operations, predict needs, and redirect savings to improve teaching quality.
          </p>
          <div className="flex gap-8 pt-4">
            <div>
              <div className="text-3xl font-bold text-white">40%</div>
              <div className="text-slate-400 text-sm">Time Saved</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">$2.5M</div>
              <div className="text-slate-400 text-sm">Avg. Savings</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">85%</div>
              <div className="text-slate-400 text-sm">Automation</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 text-slate-400 text-sm">
          © 2025 K12 ERP. All rights reserved.
        </div>
      </div>
      
      {/* Right Side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
};

export default AuthLayout;
